/**
 * Tally-style keyboard shortcuts.
 *
 * Drop-in hook mounted once at the app shell layer. Reads DOM-level
 * `data-tally-*` attributes so individual pages opt-in by simply tagging
 * their Save / Print / Add-master buttons — no per-page wiring of state
 * or callbacks needed.
 *
 * Bindings (mirroring Tally Prime defaults that Indian accountants
 * already have in their muscle memory):
 *
 *   F8                  → /invoices                       (Sales voucher)
 *   F9                  → /purchase-invoices              (Purchase voucher)
 *   Esc                 → close top-most modal/drawer, else → /dashboard
 *   Ctrl + A            → click [data-tally-save-btn]     (Accept/Save)
 *   Alt + P             → click [data-tally-print-btn]    (Print)
 *   Alt + C             → when focus is inside a Combobox tagged
 *                         data-tally-add-master-target="customer" | "vendor" | "product"
 *                         click the page-level [data-tally-add-master-btn="<same>"]
 *                         to open the "Add New …" dialog on top.
 *
 * Esc handling: shadcn Dialog already swallows Esc when open, so the
 * hook only navigates to /dashboard when no Radix-portal dialog is
 * actually visible. Drawer / Sheet share the same `[role=dialog]`
 * marker so both work transparently.
 *
 * Ctrl+A conflict: the browser's native "Select All" is preempted only
 * when the page actually wires a `[data-tally-save-btn]` — pages without
 * Save buttons keep the native behaviour intact. Inside `<input>`,
 * `<textarea>`, or `[contenteditable]` we still defer to the native
 * select-all so accountants editing a Notes field aren't surprised.
 */
import { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';

const isEditingField = (el) => {
  if (!el) return false;
  if (el.isContentEditable) return true;
  const tag = el.tagName;
  if (tag === 'INPUT' || tag === 'TEXTAREA') {
    const t = (el.getAttribute('type') || '').toLowerCase();
    // We *do* want Ctrl+A to override select-all on non-text inputs
    // (e.g. file/checkbox) but not on a Notes textarea.
    return tag === 'TEXTAREA' || !['button', 'submit', 'checkbox', 'radio', 'file', 'reset'].includes(t);
  }
  return false;
};

const findClosest = (el, selector) => {
  if (!el) return null;
  return typeof el.closest === 'function' ? el.closest(selector) : null;
};

const topMostOpenDialog = () => {
  // Radix portals stamp `data-state="open"` while a dialog is rendered.
  // Sheets / Drawers share the same primitives so the marker hits both.
  const all = document.querySelectorAll('[role="dialog"][data-state="open"]');
  return all.length ? all[all.length - 1] : null;
};

/**
 * Programmatic-close via the Radix close button if present. Falls back to
 * dispatching an `Escape` key on the Radix root so the library's own
 * keyboard handler picks it up.
 */
const closeDialog = (dialogEl) => {
  if (!dialogEl) return false;
  const closeBtn = dialogEl.querySelector('[data-radix-dialog-close], [data-state="open"] > button[aria-label="Close"]');
  if (closeBtn) {
    closeBtn.click();
    return true;
  }
  return false;
};

export function useTallyShortcuts() {
  const navigate = useNavigate();

  useEffect(() => {
    const handler = (e) => {
      const key = e.key;
      const code = e.code;

      // ----- F8 → Invoices -----
      if (key === 'F8' && !e.ctrlKey && !e.altKey && !e.metaKey) {
        e.preventDefault();
        navigate('/invoices');
        return;
      }
      // ----- F9 → Purchase Invoices -----
      if (key === 'F9' && !e.ctrlKey && !e.altKey && !e.metaKey) {
        e.preventDefault();
        navigate('/purchase-invoices');
        return;
      }

      // ----- Esc → close top modal, else go home -----
      if (key === 'Escape') {
        const open = topMostOpenDialog();
        if (open) {
          // Let Radix close it natively — only fall back if Esc wasn't
          // already intercepted by the dialog (i.e. focus elsewhere).
          if (!open.contains(document.activeElement)) {
            const fired = closeDialog(open);
            if (fired) { e.preventDefault(); return; }
          }
          // Otherwise: do nothing. The dialog will handle Esc itself.
          return;
        }
        if (window.location.pathname !== '/dashboard') {
          e.preventDefault();
          navigate('/dashboard');
        }
        return;
      }

      // ----- Ctrl+A → Save -----
      if ((e.ctrlKey || e.metaKey) && !e.altKey && !e.shiftKey && (key === 'a' || key === 'A' || code === 'KeyA')) {
        const saveBtn = document.querySelector('[data-tally-save-btn]:not([disabled])');
        if (saveBtn && !isEditingField(document.activeElement)) {
          e.preventDefault();
          saveBtn.click();
        }
        return;
      }

      // ----- Alt+P → Print -----
      if (e.altKey && !e.ctrlKey && !e.metaKey && (key === 'p' || key === 'P' || code === 'KeyP')) {
        const printBtn = document.querySelector('[data-tally-print-btn]:not([disabled])');
        if (printBtn) {
          e.preventDefault();
          printBtn.click();
        }
        return;
      }

      // ----- Alt+C → Create Master (Customer / Vendor / Product) -----
      if (e.altKey && !e.ctrlKey && !e.metaKey && (key === 'c' || key === 'C' || code === 'KeyC')) {
        // 1. Direct hit: the active element itself is tagged.
        const direct = document.activeElement?.getAttribute?.('data-tally-add-master-target');
        // 2. Or an ancestor (combobox triggers wrap a button + portal popover).
        const ancestor = findClosest(document.activeElement, '[data-tally-add-master-target]');
        const target = direct || ancestor?.getAttribute('data-tally-add-master-target');
        if (target) {
          const btn = document.querySelector(`[data-tally-add-master-btn="${target}"]:not([disabled])`);
          if (btn) {
            e.preventDefault();
            btn.click();
          }
        }
      }
    };

    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [navigate]);
}

export default useTallyShortcuts;
